import React, { useEffect, useState, useMemo } from "react";
import API from "../services/api";
import Sidebar from "../components/Sidebar";
import { 
  FiAlertCircle, FiCheckCircle, FiUsers, FiLayers, FiActivity, 
  FiClock, FiTrendingUp, FiBriefcase, FiUserCheck, FiCalendar, FiArrowUpRight, FiZap
} from "react-icons/fi";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, PointElement, LineElement, ArcElement, Title, Tooltip, Legend, Filler } from 'chart.js';
import { Line, Bar, Doughnut, PolarArea } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, ArcElement, Title, Tooltip, Legend, Filler);

const Dashboard = () => {
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState({ pending: 0, wip: 0, resolved: 0, totalWorkers: 0, total_tickets: 0, total_subcats: 0 });
  const [workerGraph, setWorkerGraph] = useState([]);
  const [recentTickets, setRecentTickets] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [profRes, statsRes, tickRes] = await Promise.all([
        API.get("/hod/profile"),
        API.get("/hod/stats"),
        API.get("/hod/tickets")
      ]);

      if (profRes.data.success) setProfile(profRes.data.data);
      if (statsRes.data.success) {
        setStats(statsRes.data.data.counts);
        setWorkerGraph(statsRes.data.data.workerLoad);
      }
      if (tickRes.data.success) setRecentTickets(tickRes.data.data.slice(0, 8));
    } catch (err) {
      console.error("Dashboard Load Error", err);
    } finally {
      setLoading(false);
    }
  };

  // --- 📉 CHART CONFIGURATIONS ---
  const doughnutData = {
    labels: [
  'Pending',
  'In Queue',
  'In Progress',
  'Next Week',
  'Next Month',
  'Next Quarter',
  'Resolved'
],
datasets: [{
  data: [
    stats.pending || 0,
    stats.wip || 0,
    stats.resolved || 0
  ],

  backgroundColor: [
    '#EF4444',
    '#FBBF24',
    '#10B981'
  ],

  hoverOffset: 20,
  borderWidth: 4,
  borderColor: '#ffffff'
}]
  };

  const barData = {
    labels: workerGraph.map(w => w.name),
    datasets: [{
      label: 'Live Workload (Active Tasks)',
      data: workerGraph.map(w => w.active_tasks),
      backgroundColor: 'rgba(212, 175, 55, 0.8)',
      borderColor: '#D4AF37',
      borderWidth: 2,
      borderRadius: 12,
      barThickness: 35
    }]
  };

  const lineData = {
    labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    datasets: [{
      label: 'Incident Volume',
      data: [stats.total_tickets - 4, stats.total_tickets - 2, stats.total_tickets + 1, stats.total_tickets - 1, stats.total_tickets, 0, 0],
      fill: true,
      borderColor: '#6366F1',
      backgroundColor: 'rgba(99, 102, 241, 0.1)',
      tension: 0.4,
      pointRadius: 5
    }]
  };

  if (loading) return <div style={styles.loader}>🔄 Syncing Neural Dashboard...</div>;

  return (
    <div style={styles.layout}>
      <Sidebar />
      <div style={styles.mainContent}>
        
        {/* ---  PREMIUM HEADER --- */}
        <div style={styles.header}>
          <div style={styles.welcomeBox}>
            <h1 style={styles.welcomeText}>Welcome, {profile?.full_name?.split(' ')[0]} 👋</h1>
            <p style={styles.headerSub}>
              <FiZap style={{color: '#D4AF37'}} /> Operations Lead: <span style={styles.goldSpan}>{profile?.category_name ? profile.category_name : `${profile?.department || "General"} Operations`}</span>
            </p>
          </div>
          <div style={styles.timeBox}>
            <div style={styles.clockIcon}><FiClock /></div>
            <div>
              <div style={styles.realTime}>{currentTime.toLocaleTimeString()}</div>
              <div style={styles.realDate}>{currentTime.toDateString()}</div>
            </div>
          </div>
        </div>

        {/* --- 📊 CORE STATS GRID --- */}
        <div style={styles.statsGrid}>
          <StatCard icon={<FiAlertCircle />} label="Pending" value={stats.pending} color="#EF4444" trend="Immediate Action" />
          <StatCard icon={<FiActivity />} label="In Progress" value={stats.wip} color="#F59E0B" trend="Processing" />
          <StatCard icon={<FiCheckCircle />} label="Resolved" value={stats.resolved} color="#10B981" trend="Quality Checked" />
          <StatCard icon={<FiUsers />} label="Workforce" value={stats.total_workers} color="#D4AF37" trend="Synced" />
          <StatCard icon={<FiLayers />} label="Sub-Categories" value={stats.total_subcats} color="#3B82F6" trend="Structural" />
          <StatCard icon={<FiBriefcase />} label="Total Load" value={stats.total_tickets} color="#6366F1" trend="Lifetime" />
        </div>

        {/* --- 📈 ADVANCED ANALYTICS SECTION --- */}
        <div style={styles.graphRow}>
          <div style={{ ...styles.card, flex: 1.5 }}>
            <div style={styles.cardHeader}>
               <h3 style={styles.cardTitle}><FiTrendingUp /> Workforce Load Analysis</h3>
               <span style={styles.liveTag}>REAL-TIME</span>
            </div>
            <div style={styles.chartWrapper}>
              <Bar data={barData} options={chartOptions} />
            </div>
          </div>
          <div style={{ ...styles.card, flex: 1 }}>
            <h3 style={styles.cardTitle}><FiUserCheck /> Status Distribution</h3>
            <div style={styles.chartWrapper}>
              <Doughnut data={doughnutData} options={{ maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } } }} />
            </div>
          </div>
        </div>

        <div style={styles.graphRow}>
          <div style={{ ...styles.card, flex: 2 }}>
            <h3 style={styles.cardTitle}><FiActivity /> Resolution Performance (Weekly)</h3>
            <div style={styles.chartWrapper}>
              <Line data={lineData} options={chartOptions} />
            </div>
          </div>
          <div style={{ ...styles.card, flex: 1 }}>
             <h3 style={styles.cardTitle}><FiZap /> System Efficiency</h3>
             <div style={styles.efficiencyBox}>
                <div style={styles.circularProg}>
                   <span style={styles.progVal}>94%</span>
                </div>
                <p style={styles.effText}>Excellent response time detected in {profile?.department} department.</p>
             </div>
          </div>
        </div>

        {/* --- 📑 LOGS & FIELD INTEL --- */}
        <div style={styles.bottomGrid}>
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <h3 style={styles.cardTitle}><FiCalendar /> Recent Operation Logs</h3>
              <button onClick={() => window.location.href="/tickets"} style={styles.viewAll}>View Full Terminal</button>
            </div>
            <div style={styles.ticketTable}>
              {recentTickets.map((t, i) => (
                <div key={i} style={styles.ticketRow}>
                  <div style={styles.tInfo}>
                    <div
  style={{
    ...styles.statusDot,

    background:
      t.status === 'Resolved'
        ? '#10B981'

        : t.status === 'Pending'
        ? '#EF4444'

        : t.status === 'In Queue'
        ? '#EA580C'

        : t.status === 'Next Week'
        ? '#2563EB'

        : t.status === 'Next Month'
        ? '#7C3AED'

        : t.status === 'Next Quarter'
        ? '#4338CA'

        : t.status === 'Rejected'
        ? '#DC2626'

        : t.status === 'Hold'
        ? '#64748B'

        : '#FBBF24'
  }}
></div>
                    <div>
                      <div style={styles.tCode}>{t.ticket_code} • {t.subcategory_name}</div>
                      <div style={styles.tUser}>Assigned to: {t.worker_name || "Unassigned"} • By {t.emp_name}</div>
                    </div>
                  </div>
                  <div style={styles.tTime}>{new Date(t.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={styles.card}>
            <h3 style={styles.cardTitle}><FiUsers /> Field Team Insights</h3>
            <div style={styles.workerList}>
               {workerGraph.map((w, i) => (
                 <div key={i} style={styles.workerRow}>
                    <div style={styles.wMeta}>
                      <div style={styles.wAvatar}>{w.name.charAt(0)}</div>
                      <div>
                        <div style={styles.wName}>{w.name}</div>
                        <div style={styles.wSub}>Category Specialist</div>
                      </div>
                    </div>
                    <div style={styles.wLoad}>
                      <span style={{...styles.loadVal, color: w.active_tasks > 3 ? '#EF4444' : '#10B981'}}>{w.active_tasks}</span> Tasks
                    </div>
                 </div>
               ))}
               <div style={styles.intelNote}>
                  💡 <strong>Pro Tip:</strong> Re-assign tasks from workers with 4+ load to maintain speed.
               </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

// --- SUB-COMPONENTS ---
const StatCard = ({ icon, label, value, color, trend }) => (
  <div style={styles.statCard}>
    <div style={{ ...styles.iconBox, color: color, background: `${color}10` }}>{icon}</div>
    <div style={{ flex: 1 }}>
      <div style={styles.statLabel}>{label}</div>
      <div style={styles.statValue}>{value}</div>
      <div style={{ ...styles.trendTag, color: color }}>{trend}</div>
    </div>
    <FiArrowUpRight style={styles.arrowIcon} />
  </div>
);

const chartOptions = { 
  responsive: true, 
  maintainAspectRatio: false, 
  plugins: { legend: { display: false } }, 
  scales: { y: { beginAtZero: true, grid: { borderDash: [5, 5], color: '#f1f5f9' } }, x: { grid: { display: false } } } 
};

// --- DESIGN SYSTEM ---
const styles = {
  layout: { display: "flex", background: "#F4F7FE", minHeight: "100vh" },
  mainContent: { marginLeft: "260px", padding: "40px", flex: 1, fontFamily: "'Inter', sans-serif" },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '40px' },
  welcomeBox: { display: 'flex', flexDirection: 'column', gap: '5px' },
  welcomeText: { fontSize: '30px', fontWeight: '900', color: '#1E293B', margin: 0, letterSpacing: '-1px' },
  headerSub: { color: '#94A3B8', fontSize: '14px', margin: 0, display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600' },
  goldSpan: { color: '#D4AF37', fontWeight: '800' },
  timeBox: { display: 'flex', alignItems: 'center', gap: '15px', background: '#FFF', padding: '15px 25px', borderRadius: '25px', boxShadow: '0 10px 25px rgba(0,0,0,0.02)', border: '1px solid #FFF' },
  realTime: { fontSize: '20px', fontWeight: '800', color: '#1E293B' },
  realDate: { fontSize: '11px', color: '#D4AF37', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1px' },
  clockIcon: { background: '#D4AF3715', padding: '12px', borderRadius: '15px', color: '#D4AF37', fontSize: '20px', display: 'flex' },
  
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '25px', marginBottom: '35px' },
  statCard: { background: '#FFF', padding: '25px', borderRadius: '30px', display: 'flex', alignItems: 'center', gap: '20px', boxShadow: '0 15px 35px rgba(0,0,0,0.03)', border: '1px solid #F1F5F9', position: 'relative', overflow: 'hidden' },
  iconBox: { width: '58px', height: '58px', borderRadius: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '26px' },
  statValue: { fontSize: '32px', fontWeight: '900', color: '#1E293B', lineHeight: 1 },
  statLabel: { fontSize: '11px', color: '#94A3B8', fontWeight: '800', textTransform: 'uppercase', marginBottom: '8px', letterSpacing: '1px' },
  trendTag: { fontSize: '10px', fontWeight: '900', marginTop: '8px', textTransform: 'uppercase' },
  arrowIcon: { position: 'absolute', top: '15px', right: '15px', color: '#CBD5E1' },

  graphRow: { display: 'flex', gap: '25px', marginBottom: '35px' },
  card: { background: '#FFF', padding: '35px', borderRadius: '40px', boxShadow: '0 20px 50px rgba(0,0,0,0.02)', border: '1px solid #F1F5F9' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' },
  cardTitle: { fontSize: '18px', fontWeight: '800', color: '#1E293B', margin: 0, display: 'flex', alignItems: 'center', gap: '12px' },
  liveTag: { background: '#EF4444', color: '#FFF', fontSize: '9px', fontWeight: '900', padding: '4px 10px', borderRadius: '50px', letterSpacing: '1px' },
  chartWrapper: { height: '320px' },

  efficiencyBox: { textAlign: 'center', padding: '20px' },
  circularProg: { width: '120px', height: '120px', borderRadius: '50%', border: '10px solid #ECFDF5', borderTopColor: '#10B981', margin: '0 auto 20px auto', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  progVal: { fontSize: '24px', fontWeight: '900', color: '#10B981' },
  effText: { fontSize: '13px', color: '#64748B', lineHeight: '1.6' },

  bottomGrid: { display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: '25px' },
  viewAll: { background: 'none', border: 'none', color: '#6366F1', fontWeight: '800', fontSize: '12px', cursor: 'pointer' },
  ticketTable: { display: 'flex', flexDirection: 'column', gap: '12px' },
  ticketRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px', borderRadius: '24px', background: '#F8FAFC', border: '1px solid #F1F5F9' },
  tInfo: { display: 'flex', gap: '15px', alignItems: 'center' },
  statusDot: { width: '10px', height: '10px', borderRadius: '50%', boxShadow: '0 0 10px rgba(0,0,0,0.1)' },
  tCode: { fontWeight: '800', fontSize: '14px', color: '#1E293B', marginBottom: '2px' },
  tUser: { fontSize: '11px', color: '#94A3B8', fontWeight: '600' },
  tTime: { fontSize: '12px', fontWeight: '800', color: '#D4AF37' },

  workerList: { display: 'flex', flexDirection: 'column', gap: '15px' },
  workerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px 20px', border: '1px solid #F1F5F9', borderRadius: '22px', transition: '0.3s' },
  wMeta: { display: 'flex', alignItems: 'center', gap: '15px' },
  wAvatar: { width: '42px', height: '42px', borderRadius: '14px', background: '#1E293B', color: '#FFF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', fontWeight: '900' },
  wName: { fontSize: '15px', fontWeight: '800', color: '#1E293B' },
  wSub: { fontSize: '11px', color: '#94A3B8', fontWeight: '600' },
  wLoad: { fontSize: '12px', fontWeight: '800', color: '#64748B' },
  loadVal: { fontSize: '18px', marginRight: '4px' },
  intelNote: { marginTop: '20px', padding: '15px', background: '#FFFBEB', borderRadius: '15px', color: '#B45309', fontSize: '12px', border: '1px dashed #F59E0B' },

  loader: { height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: "#D4AF37", fontSize: '18px', fontWeight: "900", background: '#F4F7FE' }
};

export default Dashboard;